/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactNode } from 'react';
import { Outlet, useRouteLoaderData } from 'react-router';
import type { Route } from './+types/_app';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { type ShopperProducts } from '@/scapi';
import { fetchCategory } from '@/lib/api/categories.server';
import { getLogger } from '@/lib/logger.server';
import Header from '@/components/header';
import Footer from '@/components/footer';
import { Region } from '@/components/region';
import PromoBannerSkeleton from '@/components/promo-banner/skeleton';
import { RouteErrorContent } from '@/components/route-error';
import { WishlistMergeToast } from '@/components/wishlist/wishlist-merge-toast';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';

type LoaderData = {
    root: Promise<ShopperProducts.schemas['Category']>;
    subs: Promise<ShopperProducts.schemas['Category'][]>;
    // The global footer is backed by a single Page Designer page (id `footer`). Fetched here in
    // the app-shell loader (shouldRevalidate=false → once per session) so it's not refetched on
    // every navigation. The footer falls back to hardcoded content if this is absent/errors.
    footerPage: ReturnType<typeof fetchPageWithComponentData>;
    // Global promo (pencil) banner — a single Page Designer page (id `promo-banner`) rendered above
    // the header on every page (not checkout). Fetched here in the app-shell loader (with the footer)
    // so it isn't refetched on navigation; absent/empty resolves to no banner.
    promoPage: ReturnType<typeof fetchPageWithComponentData>;
};

/**
 * We're using the `shouldRevalidate` functionality to only load the navigation menu categories from the server on the
 * very first navigation to a page using the (default) `_app` layout. Those categories requests are divided into two
 * discrete request phases; the first to only fetch the root categories, and subsequent multiple calls to fetch the
 * identified sub categories to be displayed in the navigation menu. This behavior ensures that the initial data
 * doesn't get overwritten/removed on subsequent client-side navigations.
 *
 * **Note:** If there is a desire/intent to customize that behavior, the `shouldRevalidate` function can be modified
 * accordingly. For example, a client-side storage for the categories could be introduced to implement any desired
 * caching strategy, including specific reload/refresh intervals/conditions.
 * @see {@link https://reactrouter.com/start/framework/route-module#shouldrevalidate}
 */
export function shouldRevalidate() {
    return false;
}

export function loader(args: Route.LoaderArgs): LoaderData {
    const { context } = args;
    const logger = getLogger(context);
    const config = getConfig(context);
    const { rootCategoryId, maxDepth } = config.pages.navigation;

    logger.debug('AppLayout: loader starting', { rootCategoryId, maxDepth });

    // Load the root category and its sub categories information
    // Depth 1 fetches the root category with its immediate children
    const rootCategoryPromise = fetchCategory(context, rootCategoryId, 1);

    // Load each second-level sub categories tree as well, in case the resolved root-level category has any sub
    // categories and maxDepth allows for it. We then base this composed second-level promise on the initial root
    // category promise to allow for parallel loading and streaming of the two main promises.
    const subCategoriesPromise =
        maxDepth >= 2
            ? rootCategoryPromise.then((rootCategory: ShopperProducts.schemas['Category']) =>
                  Promise.all(
                      rootCategory.categories?.reduce(
                          (
                              acc: Promise<ShopperProducts.schemas['Category']>[],
                              subCategory: ShopperProducts.schemas['Category']
                          ) => {
                              if (
                                  typeof subCategory.onlineSubCategoriesCount === 'number' &&
                                  subCategory.onlineSubCategoriesCount > 0
                              ) {
                                  acc.push(fetchCategory(context, subCategory.id, maxDepth as 0 | 1 | 2));
                              }
                              return acc;
                          },
                          []
                      ) ?? []
                  )
              )
            : Promise.resolve([]);

    return {
        root: rootCategoryPromise,
        subs: subCategoriesPromise,
        footerPage: fetchPageWithComponentData(args, { pageId: 'footer' }),
        promoPage: fetchPageWithComponentData(args, { pageId: 'promo-banner' }),
    };
}

/**
 * The standard storefront chrome — promo banner, header, main content slot, footer — shared by the
 * normal layout (`<Outlet/>`) and the `ErrorBoundary` (the 404 / error page), so a storefront error
 * renders inside the same header/promo/footer as every other page (UCR-2787).
 *
 * `<WishlistMergeToast>` stays at the app shell — it reads URL params and a one-time cookie set by
 * the post-login redirect target, not wishlist state. Routes that need wishlist hooks mount their
 * own `<DeferredWishlistProvider>` so the SCAPI call only fires on pages that render wishlist UI.
 *
 * The promo banner sits in normal flow above the sticky header — it scrolls away natively on
 * scroll-down (no height animation), so there's no transition window where it and the
 * separately-sticky header desync. Empty/absent → renders nothing and the header is flush at top.
 */
function StorefrontShell({
    promoPage,
    footerPage,
    children,
}: {
    promoPage?: LoaderData['promoPage'];
    footerPage?: LoaderData['footerPage'];
    children: ReactNode;
}) {
    return (
        <>
            <WishlistMergeToast />
            <Region
                page={promoPage ?? null}
                regionId="promo"
                fallbackElement={<PromoBannerSkeleton />}
                errorElement={null}
            />
            <Header />
            <main className="grow pt-8">{children}</main>
            <Footer footerPage={footerPage} />
        </>
    );
}

/**
 * Default Layout Route
 *
 * This pathless layout route provides the standard storefront UI structure (header, main, footer).
 * Routes that need this layout should be prefixed with `_app.` in their filename. For routes without
 * default header/footer (e.g., login), use the `_empty.` prefix instead.
 */
export default function DefaultLayout({ loaderData }: { loaderData: LoaderData }) {
    return (
        <StorefrontShell promoPage={loaderData.promoPage} footerPage={loaderData.footerPage}>
            <Outlet />
        </StorefrontShell>
    );
}

/**
 * Renders storefront errors (most visibly the 404 from the `_app.$` catch-all) inside the standard
 * chrome at the normal page width, instead of the root-level standalone error page (UCR-2787). Per
 * React Router v7, this layout-route boundary renders in place of `DefaultLayout`, so it re-renders
 * the shell itself. `_app`'s loader data (promo/footer) is available when the error came from a
 * child route (the layout loader succeeded); if the layout loader itself failed it's undefined and
 * the footer falls back to its hardcoded content. Errors outside `_app` still hit root's boundary.
 */
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
    const loaderData = useRouteLoaderData<typeof loader>('routes/_app');
    return (
        <StorefrontShell promoPage={loaderData?.promoPage} footerPage={loaderData?.footerPage}>
            <div className="section-container">
                <RouteErrorContent error={error} />
            </div>
        </StorefrontShell>
    );
}
